// src/components/ReglamentoUploadModal.js
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FaCheckCircle,
    FaCloudUploadAlt,
    FaTimes,
} from 'react-icons/fa';
import {
    collection,
    doc,
    serverTimestamp,
    setDoc,
} from 'firebase/firestore';
import {
    getDownloadURL,
    ref,
    uploadBytes,
} from 'firebase/storage';
import { db, storage } from '../firebase';
import './ReglamentoUploadModal.css';
import { registerMovement } from '../utils/registerMovement';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
const ACCEPTED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const segmentoOptions = [
    { value: 'general', label: 'Reglamento general' },
    { value: 'areas', label: 'Áreas comunes' },
];

const ReglamentoUploadModal = ({ fraccionamientoId, currentUser, userData, onClose, onSuccess }) => {
    const [segmento, setSegmento] = useState('general');
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const uploaderNombre = useMemo(() => {
        const candidates = [
            userData?.nombreCompleto,
            `${userData?.nombre || ''} ${userData?.apellidos || ''}`.trim(),
            userData?.displayName,
            currentUser?.displayName,
        ];
        for (let index = 0; index < candidates.length; index += 1) {
            const value = candidates[index];
            if (value && value.trim().length > 0) {
                return value.trim();
            }
        }
        return 'Administrador';
    }, [currentUser?.displayName, userData?.apellidos, userData?.displayName, userData?.nombre, userData?.nombreCompleto]);

    const uploaderRol = useMemo(() => {
        const raw = typeof userData?.rol === 'string' ? userData.rol : 'admin';
        return raw.trim().toLowerCase() || 'admin';
    }, [userData?.rol]);

    const resetMessages = useCallback(() => {
        setError('');
    }, []);

    const handleOverlayClick = useCallback((event) => {
        if (event.target === event.currentTarget && !saving) {
            onClose();
        }
    }, [onClose, saving]);

    const handleFileChange = useCallback((event) => {
        const selected = event.target.files && event.target.files[0];
        if (!selected) {
            return;
        }
        if (selected.size > MAX_FILE_SIZE) {
            setError('El archivo excede el límite de 20 MB.');
            return;
        }
        if (ACCEPTED_TYPES.length > 0 && !ACCEPTED_TYPES.includes(selected.type)) {
            setError('Solo se permiten archivos PDF o Word.');
            return;
        }
        setFile(selected);
        setError('');
    }, []);

    const sanitizeFileName = useCallback((name) => {
        if (!name || typeof name !== 'string') {
            return `documento_${Date.now()}`;
        }
        return name.replace(/[^\w.\-]/g, '_');
    }, []);

    const validateForm = useCallback(() => {
        if (!segmento) {
            setError('Selecciona el tipo de reglamento.');
            return false;
        }
        if (!titulo.trim()) {
            setError('Ingresa un título.');
            return false;
        }
        if (!file) {
            setError('Adjunta el archivo del reglamento.');
            return false;
        }
        setError('');
        return true;
    }, [file, segmento, titulo]);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        if (!validateForm()) {
            return;
        }
        if (!fraccionamientoId || !currentUser?.uid) {
            setError('No pudimos validar tu sesión. Intenta nuevamente.');
            return;
        }
        try {
            setSaving(true);
            resetMessages();

            const documentosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
            const documentoRef = doc(documentosRef);
            const safeName = sanitizeFileName(file.name);
            const storagePath = `fraccionamientos/${fraccionamientoId}/reglamentos/${documentoRef.id}/${safeName}`;
            const storageRef = ref(storage, storagePath);

            await uploadBytes(storageRef, file, {
                contentType: file.type || 'application/octet-stream',
            });
            const url = await getDownloadURL(storageRef);

            const payload = {
                segmento,
                titulo: titulo.trim(),
                descripcion: descripcion.trim() || null,
                fileName: file.name,
                fileSize: file.size,
                contentType: file.type || 'application/octet-stream',
                url,
                fraccionamientoId,
                createdAt: serverTimestamp(),
                uploadedBy: currentUser.uid,
                uploaderNombre,
                uploaderRol,
            };

            await setDoc(documentoRef, payload);

            try {
                await registerMovement({
                    fraccionamientoId,
                    titulo: `Reglamento publicado - ${payload.titulo}`,
                    descripcion: `Segmento: ${(segmento || 'general').toUpperCase()}${file.name ? ` · Archivo: ${file.name}` : ''}`,
                    categoria: 'Reglamentos',
                    tipoMovimiento: 'documento_publicado',
                    origen: 'reglamentos',
                    metadata: {
                        documentoId: documentoRef.id,
                        segmento,
                        fileName: file.name,
                        fileSize: file.size,
                        contentType: file.type || 'application/octet-stream',
                        cargadoPor: currentUser.uid,
                    },
                });
            } catch (logError) {
                console.error('ReglamentoUploadModal: no se pudo registrar el movimiento de publicación', logError);
            }

            setSegmento('general');
            setTitulo('');
            setDescripcion('');
            setFile(null);
            if (typeof onSuccess === 'function') {
                onSuccess('Documento publicado correctamente.');
            }
        } catch (submitError) {
            console.error('ReglamentoUploadModal: no se pudo cargar el reglamento', submitError);
            setError('No pudimos cargar el reglamento. Intenta nuevamente.');
            setSaving(false);
            return;
        }
        setSaving(false);
    }, [currentUser?.uid, descripcion, file, fraccionamientoId, onSuccess, resetMessages, sanitizeFileName, segmento, titulo, uploaderNombre, uploaderRol, validateForm]);

    const submitDisabled = saving || !file || !titulo.trim();

    return (
        <div className="reglamento-modal-overlay" role="presentation" onClick={handleOverlayClick}>
            <div className="reglamento-modal" role="dialog" aria-modal="true" aria-labelledby="reglamentoUploadTitle">
                <header className="reglamento-modal-header">
                    <div className="reglamento-header-meta">
                        <span className="reglamento-template-badge">Reglamentos</span>
                        <h2 id="reglamentoUploadTitle">Nuevo documento</h2>
                    </div>
                    <button type="button" className="reglamento-close" onClick={onClose} disabled={saving} aria-label="Cerrar">
                        <FaTimes />
                    </button>
                </header>

                {error && (
                    <div className="reglamento-error" role="alert">{error}</div>
                )}

                <div className="reglamento-body">
                    <form onSubmit={handleSubmit} className="reglamento-form">
                        <div className="reglamento-section">
                            <h3 className="reglamento-section-title">Datos del documento</h3>
                            <div className="reglamento-grid">
                                <label className="reglamento-field">
                                    <span>Tipo de reglamento *</span>
                                    <select value={segmento} onChange={(event) => setSegmento(event.target.value)} disabled={saving}>
                                        {segmentoOptions.map((option) => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="reglamento-field">
                                    <span>Título *</span>
                                    <input
                                        type="text"
                                        value={titulo}
                                        onChange={(event) => setTitulo(event.target.value)}
                                        placeholder="Nombre público"
                                        required
                                        disabled={saving}
                                    />
                                </label>
                                <label className="reglamento-field reglamento-field--full">
                                    <span>Descripción</span>
                                    <textarea
                                        value={descripcion}
                                        onChange={(event) => setDescripcion(event.target.value)}
                                        placeholder="Información adicional"
                                        rows={2}
                                        disabled={saving}
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="reglamento-section">
                            <h3 className="reglamento-section-title">Archivo</h3>
                            <div className="reglamento-upload-zone">
                                <label className={`reglamento-upload-drop ${file ? 'selected' : ''}`}>
                                    <input
                                        type="file"
                                        accept={ACCEPTED_TYPES.join(',')}
                                        onChange={handleFileChange}
                                        disabled={saving}
                                    />
                                    <FaCloudUploadAlt />
                                    <span>{file ? file.name : 'Selecciona un archivo'}</span>
                                    <small>Máximo 20 MB</small>
                                </label>
                            </div>
                        </div>

                        <footer className="reglamento-footer">
                            <button type="button" className="reglamento-btn secondary" onClick={onClose} disabled={saving}>
                                Cancelar
                            </button>
                            <button type="submit" className="reglamento-btn primary" disabled={submitDisabled}>
                                {saving ? 'Guardando...' : (
                                    <>
                                        <FaCheckCircle />
                                        <span>Publicar</span>
                                    </>
                                )}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>
        </div>
    );
};

ReglamentoUploadModal.propTypes = {
    fraccionamientoId: PropTypes.string.isRequired,
    currentUser: PropTypes.shape({
        uid: PropTypes.string,
        displayName: PropTypes.string,
    }),
    userData: PropTypes.shape({
        nombreCompleto: PropTypes.string,
        nombre: PropTypes.string,
        apellidos: PropTypes.string,
        displayName: PropTypes.string,
        rol: PropTypes.string,
    }),
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
};

ReglamentoUploadModal.defaultProps = {
    currentUser: null,
    userData: null,
    onSuccess: null,
};

export default ReglamentoUploadModal;
